import type { Email } from "@src/types/api";
import type { LoadApplication } from "@src/load-agent/LoadApplication";
import type { PostBookWorkflowSideEffects } from "@src/load-agent/domain/postBookWorkflowSideEffects";
import { runCompleteBookingTaskpaneWorkflow } from "@src/load-agent/workflow/completeBookingTaskpaneWorkflow";

export type ShipmentRowActionsApi = {
  book: (params: { mode: "LTL" | "FTL"; shipmentId: string; selectedOfferId?: string }) => Promise<{
    loadId: number;
    bolDocuments?: unknown[];
    attachmentDownloadUrls?: {
      name: string;
      url: string;
    }[];
  }>;
  select: (params: { mode: "LTL" | "FTL"; shipmentId: string; selected: boolean }) => void;
  open: (params: { mode: "LTL" | "FTL"; shipmentId: string }) => void;
};

type BookingDeps = {
  loadApp: Pick<LoadApplication, "postLoadAgentBook">;
  fx: PostBookWorkflowSideEffects;
};

export class ShipmentRowActions implements ShipmentRowActionsApi {
  constructor(
    private emailData: Email | null,
    private bookingDeps: BookingDeps,
    private onSelectionChange?: () => void,
    private onNavigate?: (mode: "LTL" | "FTL", shipmentId: string) => void,
    private onRefreshEmailData?: () => Promise<unknown>
  ) {}
  async book(params: {
    mode: "LTL" | "FTL";
    shipmentId: string;
    selectedOfferId?: string;
  }): Promise<{
    loadId: number;
    bolDocuments?: unknown[];
    attachmentDownloadUrls?: {
      name: string;
      url: string;
    }[];
  }> {
    if (!this.emailData) {
      throw new Error("No email data available for booking");
    }
    const result = await runCompleteBookingTaskpaneWorkflow(
      {
        mode: params.mode,
        shipmentId: params.shipmentId,
        email: this.emailData,
        selectedOfferId: params.selectedOfferId,
      },
      this.bookingDeps.loadApp,
      this.bookingDeps.fx
    );

    if (!result.success) {
      throw new Error(result.error || "Booking workflow failed");
    }
    if (this.onRefreshEmailData) {
      try {
        await this.onRefreshEmailData();
      } catch (error) {
        void error;
      }
    }
    return {
      loadId: result.loadId!,
      attachmentDownloadUrls: result.attachmentDownloadUrls,
    };
  }
  select(): void {
    this.onSelectionChange?.();
  }
  open(params: { mode: "LTL" | "FTL"; shipmentId: string }): void {
    this.onNavigate?.(params.mode, params.shipmentId);
  }
}
